'use client';

import { useEffect, useState } from 'react';
import {
  BadgeCheck,
  Bell,
  CheckCircle2,
  Layers3,
  ListPlus,
  DollarSign,
  LoaderCircle,
  MessageCircle,
  UsersRound,
  type LucideIcon,
} from 'lucide-react';
import { PROFESSIONAL_PLAN_BENEFITS } from '@/content/professional-plan-benefits';
import { Button } from '@/components/ui/Button';
import { ResponsiveDialog } from '@/components/ui/ResponsiveDialog';
import type { BusinessClassificationAnalyticsSource } from '@/lib/business-taxonomy';
import { trackEvent } from '@/lib/analytics';

export type ProfessionalUpsellContext =
  | 'additional-category'
  | 'professional-limit'
  | 'service-limit';

export interface ProfessionalUpsellDialogProps {
  context: ProfessionalUpsellContext;
  onClose: () => void;
  onUpgrade?: () => void | Promise<void>;
  open: boolean;
  source: BusinessClassificationAnalyticsSource;
}

const CONTEXT_COPY: Record<
  ProfessionalUpsellContext,
  {
    description: string;
    secondaryCta: string;
    title: string;
  }
> = {
  'additional-category': {
    title: 'Llega a más clientes con el Plan Profesional',
    description:
      'Agrega hasta 10 tipos de actividad y muestra todo lo que hace tu negocio.',
    secondaryCta: 'Continuar con una sola actividad',
  },
  'professional-limit': {
    title: 'Haz crecer tu equipo con el Plan Profesional',
    description:
      'El plan gratuito incluye 1 profesional. Con el Plan Profesional puedes habilitar cupos adicionales para tu equipo.',
    secondaryCta: 'Continuar con un profesional',
  },
  'service-limit': {
    title: 'Publica todos los servicios de tu negocio',
    description:
      'El plan gratuito incluye hasta 3 servicios. Con el Plan Profesional puedes agregar todos los que necesites.',
    secondaryCta: 'Continuar con mis servicios',
  },
};

const BENEFIT_ICONS: Record<
  (typeof PROFESSIONAL_PLAN_BENEFITS)[number]['id'],
  LucideIcon
> = {
  'whatsapp-reminders': Bell,
  'additional-professionals': UsersRound,
  'business-types': Layers3,
  'unlimited-services': ListPlus,
  'whatsapp-booking': MessageCircle,
  'whatsapp-verification': BadgeCheck,
  'subscription-price': DollarSign,
};

export function ProfessionalUpsellDialog({
  context,
  onClose,
  onUpgrade,
  open,
  source,
}: ProfessionalUpsellDialogProps) {
  const [isExpanded, setIsExpanded] = useState(
    context !== 'additional-category'
  );
  const [isUpgrading, setIsUpgrading] = useState(false);
  const copy = CONTEXT_COPY[context];

  useEffect(() => {
    if (!open) return;
    setIsExpanded(context !== 'additional-category');
    setIsUpgrading(false);
    trackEvent('professional_upsell_viewed', {
      source,
      upsell_context: context,
    });
  }, [context, open, source]);

  const expandBenefits = () => {
    setIsExpanded(true);
    trackEvent('professional_benefits_expanded', {
      source,
      upsell_context: context,
    });
  };

  const handleUpgrade = async () => {
    if (!onUpgrade || isUpgrading) return;

    trackEvent('professional_upgrade_clicked', {
      source,
      upsell_context: context,
    });
    setIsUpgrading(true);

    try {
      await onUpgrade();
    } finally {
      setIsUpgrading(false);
    }
  };

  const footer = isExpanded ? (
    <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.25fr)]">
      <Button type="button" variant="ghost" onClick={onClose}>
        {copy.secondaryCta}
      </Button>
      <Button
        type="button"
        onClick={() => void handleUpgrade()}
        disabled={!onUpgrade || isUpgrading}
        aria-busy={isUpgrading}
        className="disabled:cursor-not-allowed disabled:opacity-50"
      >
        {isUpgrading ? <LoaderCircle size={18} className="animate-spin" /> : null}
        Elegir Plan Profesional
      </Button>
    </div>
  ) : (
    <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.35fr)]">
      <Button type="button" variant="ghost" onClick={onClose}>
        {copy.secondaryCta}
      </Button>
      <Button
        type="button"
        onClick={expandBenefits}
        aria-expanded={isExpanded}
        aria-controls="professional-plan-benefits"
        data-dialog-autofocus
      >
        Ver todo lo que incluye el Plan Profesional
      </Button>
    </div>
  );

  return (
    <ResponsiveDialog
      open={open}
      onClose={onClose}
      title={copy.title}
      description={copy.description}
      footer={footer}
    >
      {isExpanded ? (
        <div id="professional-plan-benefits" className="space-y-4">
          <div>
            <p className="font-heading text-lg font-semibold text-text-primary">
              Todo lo que incluye el Plan Profesional
            </p>
            <p className="mt-1 text-sm leading-6 text-text-secondary">
              Más herramientas para mostrar tu negocio y gestionar tus citas.
            </p>
          </div>
          <ul className="space-y-2">
            {PROFESSIONAL_PLAN_BENEFITS.map((benefit) => {
              const Icon = BENEFIT_ICONS[benefit.id];
              return (
                <li
                  key={benefit.id}
                  className="flex items-start gap-3 rounded-card border border-border bg-bg-secondary/65 p-4"
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-button border border-success/25 bg-success/10 text-success">
                    <Icon size={18} aria-hidden="true" />
                  </span>
                  <span className="min-w-0">
                    <span className="flex items-center gap-2 font-heading text-sm font-semibold text-text-primary">
                      <CheckCircle2
                        size={16}
                        aria-hidden="true"
                        className="shrink-0 text-success"
                      />
                      {benefit.title}
                    </span>
                    <span className="mt-1 block text-sm leading-6 text-text-secondary">
                      {benefit.description}
                    </span>
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
    </ResponsiveDialog>
  );
}
